<script lang="ts" setup>
	const { getGuidesCategories } = useProductGuideApi()
	const loading = shallowRef(false)

	const { status, data } = useLazyAsyncData(() => getGuidesCategories())
	const isLoading = computed(() => status.value === 'pending' || loading.value)
	const icons: Record<
		| 'onboarding'
		| 'buy-plans'
		| 'hospital-visit'
		| 'inspection-claim'
		| 'manage-plan'
		| 'support'
		| 'transaction-guide',
		{ default: string; active: string }
	> = {
		onboarding: {
			default: '/img/product-guide/hand-shake.svg',
			active: '/img/product-guide/hand-shake-active.svg'
		},
		'buy-plans': {
			default: '/img/product-guide/cart.svg',
			active: '/img/product-guide/cart-active.svg'
		},
		'hospital-visit': {
			default: '/img/product-guide/hospital.svg',
			active: '/img/product-guide/hospital-active.svg'
		},
		'inspection-claim': {
			default: '/img/product-guide/inspection.svg',
			active: '/img/product-guide/inspection-active.svg'
		},
		'manage-plan': {
			default: '/img/product-guide/management.svg',
			active: '/img/product-guide/management-active.svg'
		},
		support: {
			default: '/img/product-guide/support.svg',
			active: '/img/product-guide/support-active.svg'
		},
		'transaction-guide': {
			default: '/img/product-guide/wishlist.svg',
			active: '/img/product-guide/wishlist-active.svg'
		}
	}
	const guideCategory = computed(() => {
		if (data.value && Array.isArray(data.value)) {
			return data.value.map(category => {
				const k = String(category.slug) as keyof typeof icons
				return {
					...category,
					icons: icons[k]
				}
			})
		}
		return []
	})
	useSeoMeta({
		title: 'Insurance Guide - Plans, Claims, & Coverage Details Explained',
		description:
			'Discover the MyCoverGenius Insurance Product Guide. Your go-to resource for understanding policies, managing claims, and maximizing coverage.',
		ogTitle: 'Insurance Guide - Plans, Claims, & Coverage Details Explained',
		ogDescription:
			'Discover the MyCoverGenius Insurance Product Guide. Your go-to resource for understanding policies, managing claims, and maximizing coverage.',
		ogImage: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082913/team-office_im9gfo.webp',
		ogUrl: 'https://www.mycovergenius.com/product-guide',
		twitterTitle: 'Insurance Guide - Plans, Claims, & Coverage Details Explained',
		twitterDescription:
			'Discover the MyCoverGenius Insurance Product Guide. Your go-to resource for understanding policies, managing claims, and maximizing coverage.',
		twitterImage: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082913/team-office_im9gfo.webp',
		twitterCard: 'summary_large_image',
		twitterSite: '@MyCoverGenius',
		twitterCreator: '@MyCoverGenius',
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
</script>

<template>
	<div>
		<header
			class="md:min-h-[420px] min-h-[300px] sm:min-h-[400px] relative mt-0 bg-base-800 dark:bg-base-900 pt-12 xs:pt-16"
		>
			<ui-pad>
				<ClientOnly>
					<ui-h1
						tag="Product Guide"
						:tag-icon="{ dir: 'document', icon: 'book' }"
						class="md:flex items-center flex-col space-y-3 justify-center text-left"
						:ui="{
							btn: { base: 'hidden' },
							paragraphClass: {
								maxWidth: ''
							},
							hClass: { wrapper: { base: 'sm:pt-5' } },
							tag: {
								base: 'bg-[rgba(255,255,255,0.2)] text-white backdrop-blur-[10px]',
								default: '',
								font: 'font-[700]',
								size: 'lg:text-xl text-xs',
								icon: { display: 'md:hidden' },
								padding: 'md:p-[12px_24px] p-[4px_12px]',
								rounded: 'rounded-[20px]'
							}
						}"
					>
						<template #title="{ highlightColor }">
							<span>
								Get Detailed Information About <span :class="highlightColor">Our Products</span>
							</span>
						</template>
					</ui-h1>
				</ClientOnly>
			</ui-pad>
		</header>

		<ui-spacer :ui="{ marginBlock: 'my-20 lg:my-0' }" />

		<ui-pad :ui="{ paddingInline: 'w-full sm:px-10' }">
			<div class="md:py-14 pb-8 w-full">
				<template v-if="isLoading">
					<div
						class="w-full grow flex flex-wrap items-stretch lg:flex sm:grid place-content-center xl:place-content-start sm:grid-cols-2 gap-10"
					>
						<ui-skeleton v-for="i in 3" :key="`loading-${i}`" class="w-full" />
					</div>
				</template>
				<template v-else>
					<div
						class="flex sm:grid grid-cols-2 lg:flex flex-wrap overflow-hidden w-full justify-center gap-x-5 gap-y-10"
					>
						<nuxt-link
							v-for="(category, index) in guideCategory"
							:key="`guide-${index + 1}`"
							:to="Number(category.count) > 0 ? `/product-guide/${category.id}-${category.slug}` : ''"
							class="flex justify-center flex-col flex-none items-center relative bg-white rounded-lg hover:border-2 hover:border-main group border border-[#cfd4dc] overflow-hidden py-[61px] px-[10px] max-w-[322px] custom-transition-2 lg:px-[40px] w-full"
						>
							<div class="text-center">
								<div class="pb-1 mx-auto mb-[20px] w-full flexed justify-center relative">
									<img
										:src="category.icons.default"
										class="lg:max-w-[66.67px] custom-transition-3 group-hover:hidden group-hover:absolute max-w-[80px] w-full object-contain"
										loading="lazy"
										:alt="category.slug"
									/>
									<img
										:src="category.icons.active"
										class="lg:max-w-[66.67px] transition group-hover:block hidden max-w-[80px] w-full object-contain"
										loading="lazy"
										:alt="category.slug"
									/>
								</div>
								<h4
									class="text-blue-950 mb-[12px] transition ease-linear group-hover:font-bold group-hover:text-base-800 text-xl font-semibold leading-7"
								>
									{{ category.name }}
								</h4>
								<div
									class="px-3 group-hover:bg-base-50 custom-transition-2 py-1.5 bg-[#f2f3f6] rounded-3xl inline-flex justify-center items-center gap-2.5"
								>
									<span
										class="text-[#344053] lg:text-base text-xs group-hover:font-semibold font-medium leading-[18.37px] lg:leading-snug"
									>
										{{ category.count ?? 0 }} Article{{ Number(category.count) > 1 ? 's' : '' }}
									</span>
								</div>
							</div>
						</nuxt-link>
					</div>
				</template>
			</div>
		</ui-pad>
	</div>
</template>
